import { useState, KeyboardEvent, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../Input';
import styles from './TagInput.module.css';

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  maxTagLength?: number;
}

export interface TagInputRef {
  flushPendingTag: () => string[];
}

export const TagInput = forwardRef<TagInputRef, TagInputProps>(({
  tags,
  onChange,
  placeholder,
  maxTags = 20,
  maxTagLength = 50,
}, ref) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addTag = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    
    if (!trimmed) {
      return;
    }

    if (tags.length >= maxTags) {
      setError(`Maximum ${maxTags} tags allowed`);
      return;
    }

    if (trimmed.length > maxTagLength) {
      setError(`Tag must be less than ${maxTagLength} characters`);
      return;
    }

    if (tags.includes(trimmed)) {
      setError('Tag already exists');
      return;
    }

    onChange([...tags, trimmed]);
    setInputValue('');
    setError(null);
  };

  // Expõe método para adicionar tag pendente
  useImperativeHandle(ref, () => ({
    flushPendingTag: () => {
      if (inputValue.trim()) {
        const trimmed = inputValue.trim().toLowerCase();
        
        // Validações
        if (tags.length >= maxTags || trimmed.length > maxTagLength || tags.includes(trimmed)) {
          return tags;
        }
        
        const newTags = [...tags, trimmed];
        onChange(newTags);
        setInputValue('');
        setError(null);
        return newTags;
      }
      return tags;
    },
  }));

  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag if input is empty and backspace is pressed
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className={styles.container}>
      {tags.length > 0 && (
        <div className={styles.tagsContainer}>
          {tags.map((tag) => (
            <div key={tag} className={styles.tag}>
              <span className={styles.tagText}>#{tag}</span>
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => removeTag(tag)}
                aria-label={`Remove ${tag}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || t('linkDetail.tagsPlaceholder')}
        aria-label="Add tag"
      />
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
});

